import {
  ProductDataVariant,
  TagGroup,
  Tag
} from '../../../../services/product/product-data'
import type { GroupedItem, ProductListConfig } from '../models'

type DataType = {
  items: GroupedItem[]
  facets: Array<TagGroup>
  gcodes: string[]
}

export type FilterStashType = {
  [filterId: TagGroup['tagGroupId']]: Tag['tagId'][]
}

export type EnabledFiltersType = {
  [tagGroupId: string]: string[]
}

export type HistoryFilter = {
  filterId: string
  value: string
}

export const showProduct = (
  variants: Partial<ProductDataVariant>[],
  stash: FilterStashType
) =>
  Object.entries(stash).every(
    ([key, value]) =>
      !value?.length ||
      variants.some((v) =>
        v?.tags?.find((t) => t.tagGroupId === key && value.includes(t.tagId))
      )
  )

const hasMatchingTag = (variant: Partial<ProductDataVariant>, option: Tag) => {
  return variant?.tags?.find((t) => t?.tagId === option.tagId)
}

const hasVariantWithMatchingTag = (product: GroupedItem, option: Tag) => {
  return product.variants.some((v) => hasMatchingTag(v, option))
}

const checkIsOptionEnable = (productList: GroupedItem[]) => (option: Tag) => {
  return productList.some((p) => hasVariantWithMatchingTag(p, option))
}

export const getEnabledFilters = (
  data: DataType,
  filters: ProductListConfig['filters'],
  filtersStash: FilterStashType
): EnabledFiltersType => {
  const products: GroupedItem[] = data?.items ?? []
  return (
    filters?.filterItems?.reduce((acc, { tagID: tagGroupId }) => {
      const facet = data?.facets?.find((f) => f?.tagGroupId === tagGroupId)
      const filterOptions = facet?.tags ?? []
      const appliedFiltersExcludingCurr = Object.entries(filtersStash)?.reduce(
        (applied, [key, value]) => ({
          ...applied,
          ...(key !== tagGroupId ? { [key]: value } : {})
        }),
        {}
      )
      const productsExcludingCurrFilter = products.filter((p) =>
        showProduct(p.variants, appliedFiltersExcludingCurr)
      )

      return {
        ...acc,
        ...(facet
          ? {
              [tagGroupId]: filterOptions
                .filter(checkIsOptionEnable(productsExcludingCurrFilter))
                .map((f) => f.tagId)
            }
          : {})
      }
    }, {}) ?? {}
  )
}

export const filterHistoryByStash = (
  filterStash: FilterStashType,
  prevHistoryState: HistoryFilter[],
  enabledHistoryFilterIds: string[]
) =>
  prevHistoryState.filter(({ filterId, value }) =>
    Object.entries(filterStash)?.find(
      ([filterKey, filterValues]) =>
        filterKey === filterId &&
        enabledHistoryFilterIds.includes(filterId) &&
        filterValues.includes(value)
    )
  )

export const getHistoryFromStash = (
  stash: FilterStashType,
  enabledHistoryFilterIds: string[]
) =>
  Object.entries(stash)?.reduce((acc: HistoryFilter[], [key, values]) => {
    if (enabledHistoryFilterIds.includes(key)) {
      const formattedValues = values.map((v) => ({
        filterId: key,
        value: v
      }))
      return [...acc, ...formattedValues]
    }
    return acc
  }, [])

export const getMatchedVariant = (
  variants: ProductDataVariant[],
  historyFilters: {
    filterId: string
    value: string
  }[]
) => {
  for (let i = historyFilters.length - 1; i >= 0; i -= 1) {
    const { filterId, value } = historyFilters[i]
    const variant = variants.find((v) =>
      v?.tags?.some((t) => t.tagGroupId === filterId && t.tagId === value)
    )
    if (variant) {
      return variant
    }
  }
  return undefined
}
